import { useQuery } from "@apollo/client";
import { useEffect, useMemo } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Button } from "antd";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { IoIosArrowRoundBack } from "react-icons/io";
import Breadcrumb from "../../components/Breadcrumb";
import { EmptyState, ErrorState } from "../../components/states";
import { useToast } from "../../components/Toast/toastHook";
import BuyerMatches from "../../components/upcomingListings/BuyerMatches";
import DetailBlock, { DetailRow } from "../../components/upcomingListings/DetailBlock";
import ListingPhotoGallery from "../../components/upcomingListings/ListingPhotoGallery";
import ListingSpecStrip from "../../components/upcomingListings/ListingSpecStrip";
import UpcomingListingDetailSkeleton from "../../components/upcomingListings/UpcomingListingDetailSkeleton";
import {
  formatAddress,
  formatAgentContact,
  formatAgentCredentials,
  formatAgentName,
  formatCityLine,
  formatLongDate,
  formatOpenHouse,
  formatPrice,
  formatPricePerSqFt,
  formatPropertyCategory,
  formatPropertyType,
} from "../../components/upcomingListings/listingDisplay";
import { goLiveLabel } from "../../utils/goLive";
import {
  GET_UPCOMING_LISTINGS,
  GET_UPCOMING_LISTING_DETAIL,
} from "../../graphql/listingQueries";
import {
  ALL_AGENTS,
  DEFAULT_SORT,
  SIBLING_FETCH_LIMIT,
  UPCOMING_WINDOW_DAYS,
} from "../../constants/upcomingListingsConstants";

/**
 * One upcoming listing in full — what a broker clicks into mid-meeting.
 *
 * Prev / Next walk the same filtered, sorted set the list view was showing. The
 * filters live in the query string rather than router state, so a bookmarked or
 * refreshed detail page still knows which listing comes next.
 */
const UpcomingListingDetail = () => {
  const { companyId, listingId } = useParams();
  const navigate = useNavigate();
  const toast = useToast(4000);
  const [searchParams] = useSearchParams();

  const agentId = searchParams.get("agent") || ALL_AGENTS;
  const sortBy = searchParams.get("sort") || DEFAULT_SORT;
  const listPath = `/company/${companyId}/upcoming-listings`;
  const backTo = searchParams.toString()
    ? `${listPath}?${searchParams.toString()}`
    : listPath;

  const { data, loading, error, refetch } = useQuery(GET_UPCOMING_LISTING_DETAIL, {
    variables: { listingId },
    fetchPolicy: "cache-and-network",
    skip: !listingId,
  });

  // The sibling set exists only to drive Prev / Next, so it is served from cache
  // when the broker has just come from the list.
  const { data: siblingData } = useQuery(GET_UPCOMING_LISTINGS, {
    variables: {
      companyId,
      listingInput: {
        upcomingOnly: true,
        upcomingWithinDays: UPCOMING_WINDOW_DAYS,
        ...(agentId !== ALL_AGENTS && { agentId }),
      },
      pagination: { page: 1, limit: SIBLING_FETCH_LIMIT },
      sortBy,
    },
    fetchPolicy: "cache-first",
  });

  const listing = data?.getListingById;

  const { prev, next, position } = useMemo(() => {
    const siblings = siblingData?.getCompanyListings?.listing ?? [];
    const at = siblings.findIndex((l) => l._id === listingId);
    if (at === -1) return { prev: null, next: null, position: null };

    return {
      prev: siblings[at - 1] ?? null,
      next: siblings[at + 1] ?? null,
      position: { current: at + 1, total: siblings.length },
    };
  }, [siblingData, listingId]);

  // Secondary actions carry the brand outline the rest of the admin uses. Left
  // off when disabled so antd's own disabled styling shows through — a greyed
  // button still reads as a button, a green one would look clickable.
  const outline = { borderColor: "#b4c540", color: "#96aa10" };

  const goTo = (sibling) => {
    const query = searchParams.toString();
    navigate(`${listPath}/${sibling._id}${query ? `?${query}` : ""}`);
  };

  useEffect(() => {
    if (error) toast("error", error.message);
  }, [error, toast]);

  if (loading && !listing) return <UpcomingListingDetailSkeleton />;

  if (error && !listing) {
    return (
      <div className="pb-10">
        <Breadcrumb pageName="Upcoming Listings" />
        <ErrorState onRetry={() => refetch()} />
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="pb-10">
        <Breadcrumb pageName="Upcoming Listings" />
        <EmptyState
          title="Listing not found"
          description="It may have been deleted since the list was loaded."
          actionLabel="Back to Upcoming Listings"
          onAction={() => navigate(backTo)}
        />
      </div>
    );
  }

  const address = formatAddress(listing);
  const cityLine = formatCityLine(listing);
  const price = formatPrice(listing.price);
  const perSqFt = formatPricePerSqFt(listing);
  const goesLive = formatLongDate(listing.expiration);
  const badge = goLiveLabel(listing.expiration);
  const propertyType =
    [formatPropertyCategory(listing.propertyCategory), formatPropertyType(listing.propertyType)]
      .filter(Boolean)
      .join(" · ") || null;

  const agentName = formatAgentName(listing.user);
  const agentContact = formatAgentContact(listing.user);
  const agentCredentials = formatAgentCredentials(listing.user, listing.office);

  const openHouses = (listing.openHouses ?? []).filter((oh) => oh?.startTime);
  const amenities = (listing.amenities ?? []).filter((a) => a?.title);
  const links = (listing.links ?? []).filter((l) => l?.url);
  // Mirrors the mobile app's "Listing History": what changed and when, with the
  // original listing date closing the list.
  const history = (listing.changes ?? [])
    .filter((c) => c?.key)
    .map((c) => ({ label: c.key, when: formatLongDate(c.modified) }));
  const listedOn = formatLongDate(listing.createdAt);

  const [lng, lat] = listing.coordinates ?? [];
  const mapsKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  return (
    <div id="upcoming-listing-detail" className="pb-10">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          {/* Styled like the app's other back links (AgentListingView) so it
              reads as a control rather than a caption. */}
          <Link
            to={backTo}
            className="-ml-1 inline-flex items-center gap-0.5 text-sm font-medium text-primary hover:underline"
          >
            <IoIosArrowRoundBack size={24} />
            Back to Upcoming Listings
          </Link>
          <h1 className="text-3xl font-semibold text-primary">
            {address ?? "Listing"}
          </h1>
        </div>

        <div className="flex items-center gap-2">
          {position && (
            <span
              aria-label={`Listing ${position.current} of ${position.total}`}
              className="mr-1 rounded-sm border border-light_gray bg-white px-3 py-1 text-xs text-secondary"
            >
              <b className="text-primary_dark">{position.current}</b> of{" "}
              {position.total}
            </span>
          )}
          {/* Labelled explicitly so they are never confused with the photo
              arrows sitting a few pixels below. */}
          <Button
            aria-label="Previous listing"
            icon={<LeftOutlined />}
            disabled={!prev}
            onClick={() => goTo(prev)}
            style={prev ? outline : undefined}
          >
            Prev
          </Button>
          <Button
            aria-label="Next listing"
            disabled={!next}
            onClick={() => goTo(next)}
            style={next ? outline : undefined}
          >
            Next <RightOutlined />
          </Button>
          {/* Presenting starts on the listing being looked at, not back at the
              top of the list. */}
          <Button
            type="primary"
            onClick={() => {
              const query = new URLSearchParams(searchParams);
              query.set("present", "1");
              query.set("start", listingId);
              navigate(`${listPath}?${query.toString()}`);
            }}
            style={{ backgroundColor: "#96aa10", borderColor: "#96aa10" }}
          >
            ▶ Present
          </Button>
        </div>
      </div>
      <div className="mb-6 block w-full border-b-2 border-primary" />

      <div className="flex flex-col gap-4 lg:flex-row">
        <div className="min-w-0 lg:flex-[1.25]">
          <ListingPhotoGallery photos={listing.imageURLs ?? []} badge={badge} />

          <ListingSpecStrip listing={listing} />

          <DetailBlock title="Property">
            <DetailRow label="Property Type" value={propertyType} />
            <DetailRow label="Buyer Commission" value={listing.buyer_commission} />
            <DetailRow label="Listed On" value={formatLongDate(listing.createdAt)} />
            <DetailRow label="Goes Live on MLS" value={goesLive} />
          </DetailBlock>

          {listing.notes && (
            <DetailBlock title="Notes">
              <p className="whitespace-pre-line text-sm text-secondary">
                {listing.notes}
              </p>
            </DetailBlock>
          )}

          <BuyerMatches listingId={listingId} />

          {(history.length > 0 || listedOn) && (
            <DetailBlock title="Listing History">
              {history.map((h, i) => (
                <div key={i} className="flex justify-between gap-4 py-0.5 text-sm">
                  <span className="text-secondary">{h.label}</span>
                  <span className="text-gray-400">{h.when}</span>
                </div>
              ))}
              {listedOn && (
                <div className="flex justify-between gap-4 py-0.5 text-sm">
                  <span className="text-secondary">Created</span>
                  <span className="text-gray-400">{listedOn}</span>
                </div>
              )}
            </DetailBlock>
          )}

          {amenities.length > 0 && (
            <DetailBlock title="Amenities">
              <div className="flex flex-wrap gap-2">
                {amenities.map((a) => (
                  <span
                    key={a._id}
                    className="rounded-sm bg-light_green px-2 py-1 text-xs text-secondary"
                  >
                    {a.title}
                  </span>
                ))}
              </div>
            </DetailBlock>
          )}
        </div>

        <div className="min-w-0 lg:flex-1">
          <DetailBlock>
            {badge && (
              <span className="inline-block rounded-sm bg-blue px-2 py-1 text-[11px] font-extrabold tracking-wide text-white">
                {badge}
              </span>
            )}
            {price && (
              <div className="mt-2 text-3xl font-black text-primary_dark">{price}</div>
            )}
            {(address || cityLine) && (
              <div className="text-sm text-secondary">
                {[address, cityLine].filter(Boolean).join(", ")}
              </div>
            )}
            {(perSqFt || goesLive) && (
              <div className="mt-1 text-xs text-gray-400">
                {[perSqFt, goesLive && `Goes live ${goesLive}`]
                  .filter(Boolean)
                  .join(" · ")}
              </div>
            )}
          </DetailBlock>

          {openHouses.length > 0 && (
            <DetailBlock title="Open House">
              {openHouses.map((oh, i) => (
                <div key={i} className="text-sm text-secondary">
                  {formatOpenHouse(oh)}
                  {oh.needHost && (
                    <span className="font-bold text-red"> · Host needed</span>
                  )}
                </div>
              ))}
            </DetailBlock>
          )}

          {lat && lng && mapsKey && (
            <DetailBlock title="Location">
              <iframe
                title="Listing location"
                width="100%"
                height="200"
                style={{ border: 0, borderRadius: 2 }}
                loading="lazy"
                src={`https://www.google.com/maps/embed/v1/place?key=${mapsKey}&q=${lat},${lng}`}
              />
            </DetailBlock>
          )}

          {agentName && (
            <DetailBlock title="Listing Agent">
              <div className="flex items-start gap-3">
                <span className="mt-0.5 h-9 w-9 shrink-0 rounded-full bg-light_green" />
                <div>
                  <div className="text-sm font-extrabold text-dark_gray">{agentName}</div>
                  {agentContact && (
                    <div className="text-xs text-secondary">{agentContact}</div>
                  )}
                  {agentCredentials && (
                    <div className="text-[11px] text-gray-400">{agentCredentials}</div>
                  )}
                </div>
              </div>
            </DetailBlock>
          )}

          {links.length > 0 && (
            <DetailBlock title="Links" className="mb-0">
              {links.map((l, i) => (
                <a
                  key={i}
                  href={l.url}
                  target="_blank"
                  rel="noreferrer"
                  className="block text-sm text-blue hover:underline"
                >
                  🔗 {l.title || l.url}
                </a>
              ))}
            </DetailBlock>
          )}
        </div>
      </div>
    </div>
  );
};

export default UpcomingListingDetail;
